import React, { useEffect, useState } from "react";
import { showError, showSuccess } from "../../utils/toastUtils";
import { useLoader } from "../../utils/LoaderUtils";
import { useParams } from "react-router-dom";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import SubmitButton from "../../components/partials/global/SubmitButton";
import supplierAPI from "../../api/inventory/supplierApi";

function Edit() {
  useAlphabeticInput();

  const params = useParams();
  const id = params.id || {};

  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    contactPerson: "",
    mobile: "",
    email: "",
    gstin: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    district: "",
    pincode: "",
    paymentTermDays: "",
    notes: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "pincode") {
      handlePincodeChange(value);
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };


  const handlePincodeChange = async (value) => {
    setFormData((prev) => ({ ...prev, pincode: value }));

    if (value.length === 6) {
      const res = await fetch(`https://api.postalpincode.in/pincode/${value}`);
      const data = await res.json();

      if (data[0].Status === "Success") {
        setFormData((prev) => ({
          ...prev,
          state: data[0].PostOffice[0].State,
          district: data[0].PostOffice[0].District,
          country: data[0].PostOffice[0].Country,
          city: data[0].PostOffice[0].City,
        }));
      } else {
        showError("Invalid Pincode");
      }
    }
  };

  useEffect(() => {
    const fetchSingleDetails = async () => {
      try {
        const res = await supplierAPI.getSingleDetails(id);

        if (res.code === 200) {
          const result = res.data;

          setFormData({
            name: result.name,
            code: result.code,
            contactPerson: result.contactPerson,
            mobile: result.mobile,
            email: result.email,
            gstin: result.gstin,
            address1: result.address1,
            address2: result.address2,
            city: result.city,
            district: result.district,
            state: result.state,
            pincode: result.pincode,
            country: result.country,
            paymentTermDays: result.paymentTermDays,
            notes: result.notes,
          });

          setOriginalData({
            name: result.name,
            code: result.code,
            contactPerson: result.contactPerson,
            mobile: result.mobile,
            email: result.email,
            gstin: result.gstin,
            address1: result.address1,
            address2: result.address2,
            city: result.city,
            district: result.district,
            state: result.state,
            pincode: result.pincode,
            country: result.country,
            paymentTermDays: result.paymentTermDays,
            notes: result.notes,
          });
        } else {
          showError(res.message);
        }
      } catch (err) {
        showError(err.message);
      }
    };

    fetchSingleDetails();
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!formData.name) {
      newErrors.name = "Name is required";
    }

    if (!formData.code) {
      newErrors.code = "Code is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getChangedFields = (original, current) => {
    const changed = {};

    Object.keys(current).forEach((key) => {
      if (current[key] !== original[key]) {
        // empty / null image ignore
        if (key === "image" && !current[key]) return;
        changed[key] = current[key];
      }
    });

    return changed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    showLoader();

    if (!validate()) {
      setIsLoading(false);
      hideLoader();
      return;
    }

    try {
      const payload = getChangedFields(originalData, formData);

      if (Object.keys(payload).length === 0) {
        showError("No changes detected");
        setIsLoading(false);
        hideLoader();
        return;
      }

      const res = await supplierAPI.updateDetails(id, payload);

      if (res.code === 200) {
        showSuccess(res.message);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Add New Supplier</h4>
            <h6>
              Create a new supplier to manage your inventory and purchase
              invoices.
            </h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Add Details</h4>
          </div>
          <div className="card-body profile-body">
            <form method="post" onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-lg-4 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                    {errors.name && (
                      <span className="errors text-danger">{errors.name}</span>
                    )}
                  </div>
                </div>
                <div className="col-lg-4 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Code"
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                    />
                    {errors.code && (
                      <span className="errors text-danger">{errors.code}</span>
                    )}
                  </div>
                </div>
                <div className="col-lg-4 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      GSTIN <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="GSTIN"
                      name="gstin"
                      value={formData.gstin}
                      onChange={handleChange}
                    />
                    {errors.gstin && (
                      <span className="errors text-danger">{errors.gstin}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-4 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Contact Person Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control only-alphabets"
                      placeholder="Contact Person Name"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleChange}
                    />
                    {errors.contactPerson && (
                      <span className="errors text-danger">
                        {errors.contactPerson}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-4 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Mobile <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control only-numeric"
                      placeholder="Mobile"
                      maxLength={10}
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                    />
                    {errors.mobile && (
                      <span className="errors text-danger">
                        {errors.mobile}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-4 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Email Address"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {errors.email && (
                      <span className="errors text-danger">{errors.email}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <h6>Address Information</h6>
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Address 1 <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Address Line 1"
                      name="address1"
                      value={formData.address1}
                      onChange={handleChange}
                    />
                    {errors.address1 && (
                      <span className="errors text-danger">
                        {errors.address1}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Address 2 <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Address Line 2"
                      name="address2"
                      value={formData.address2}
                      onChange={handleChange}
                    />
                    {errors.address2 && (
                      <span className="errors text-danger">
                        {errors.address2}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-4 col-sm-4">
                  <div className="mb-3">
                    <label className="form-label">
                      PinCode<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control only-numeric"
                      maxLength={6}
                      name="pincode"
                      placeholder="Pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                    />
                    {errors.pincode && (
                      <span className="errors text-danger">
                        {errors.pincode}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-4 col-sm-4">
                  <div className="mb-3">
                    <label className="form-label">
                      City<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="City"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                    />
                    {errors.city && (
                      <span className="errors text-danger">{errors.city}</span>
                    )}
                  </div>
                </div>
                <div className="col-lg-4 col-sm-4">
                  <div className="mb-3">
                    <label className="form-label">
                      District<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      readOnly
                      placeholder="District"
                      name="district"
                      value={formData.district}
                      onChange={handleChange}
                    />
                    {errors.district && (
                      <span className="errors text-danger">
                        {errors.district}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-4 col-sm-4">
                  <div className="mb-3">
                    <label className="form-label">
                      State<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      readOnly
                      placeholder="State"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                    />
                    {errors.state && (
                      <span className="errors text-danger">{errors.state}</span>
                    )}
                  </div>
                </div>
                <div className="col-lg-4 col-sm-4">
                  <div className="mb-3">
                    <label className="form-label">
                      Country<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      readOnly
                      placeholder="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                    />
                    {errors.country && (
                      <span className="errors text-danger">
                        {errors.country}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <h6>Other Information</h6>
                </div>
                <div className="col-lg-6 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Payment Terms Days<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="paymentTermDays"
                      placeholder="Day Count"
                      value={formData.paymentTermDays}
                      onChange={handleChange}
                    />
                    {errors.paymentTermDays && (
                      <span className="errors text-danger">
                        {errors.paymentTermDays}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <label className="form-label">
                    Notes <span className="text-danger">*</span>
                  </label>
                  <textarea
                    value={formData.message}
                    className="form-control"
                    placeholder="Notes"
                    name="message"
                    onChange={handleChange}
                  ></textarea>
                  {errors.message && (
                    <span className="errors text-danger">{errors.message}</span>
                  )}
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-12 d-flex justify-content-end">
                  <SubmitButton
                    isLoading={isLoading}
                    text="Update Details"
                    loadingText="Updating..."
                  ></SubmitButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default Edit;
